/**
 * English strings for the `recording` namespace. The source of truth: French is
 * typed against this file. See `apps/web/src/i18n/README.md`.
 */
const recording = {
  toast: {
    translating: "Translating — subtitles switch over when it finishes.",
    translationCancelled: "Translation cancelled.",
    transcribing: "Transcribing. This page updates when it is done.",
  },
  notFound: {
    title: "Recording not found",
    description: "It may have been deleted, or belong to a different workspace.",
  },
  loadError: "Couldn't load this recording",
  pending: {
    fallback: "This recording is still being prepared.",
    stop: "Stop recording",
    openLink: "Open the link",
    backToLibrary: "Back to library",
  },
  preparing: {
    title: "Preparing this recording for streaming",
    body: "It will start instantly and seek instantly once this finishes. The transcript is ready now.",
    playAnyway: "Play the original anyway",
  },
  renameLabel: "Rename this recording",
  captions: {
    sizeLabel: "Subtitle size",
    sizeTitle: {
      s: "Small subtitles",
      m: "Medium subtitles",
      l: "Large subtitles",
    },
    /** The single letter on each size button (shown uppercase). */
    sizeLetter: {
      s: "s",
      m: "m",
      l: "l",
    },
    languageTitle:
      "Subtitle language — new languages are translated with the workspace's AI model, after you confirm",
    original: "Original",
    translations: "Translations",
    translateTo: "Translate to…",
    optionTranslating: "{{language}} · translating…",
    optionFailed: "{{language}} · failed, pick to retry",
    cancelTitle: "Stop this translation — it stops billing at the next batch",
    cancel: "✕ cancel",
  },
  participants_one: "{{count}} participant",
  participants_other: "{{count}} participants",
  studio: {
    button: "Edit in Studio",
    title: "Cut, reorder and re-chapter this recording",
  },
  export: {
    button: "Download / Export",
    title: "Download the original, or export with subtitles burned in",
  },
  importedFrom: "Imported from {{source}}",
  importedFromLink: "Imported from a link",
  importedFromTitle: "Open the video this was imported from",
  editedFrom: "Edited from “{{title}}”",
  editedFromTitle: "This recording was published from a Studio edit",
  edits: "Edits:",
  noTranscript: {
    title: "No transcript",
    transcribe: "Transcribe now",
  },
  streaming: {
    preparing: "Preparing for streaming",
    notPrepared: "Streaming not prepared",
    preparingBody:
      "This takes a few minutes on a long recording. You can watch now — playback becomes instant once it finishes.",
    notPreparedBody:
      "This plays from the original file, so it downloads before it starts. Encoding it for streaming makes playback and seeking immediate.",
    encoding: "Encoding",
    prepare: "Prepare streaming",
  },
  tabs: {
    transcript: "Transcript",
    chapters: "Chapters",
    ask: "Ask",
  },
  layout: {
    mergeTitle: "Merge each speaker's lines into paragraphs",
    splitTitle: "Show each line separately",
    lines: "Lines",
    paragraphs: "Paragraphs",
  },
  chapters: {
    regenerating: "Regenerating",
    regenerate: "Regenerate",
    dividing: "Dividing the meeting into chapters.",
    failed: "Chapters could not be generated.",
    empty:
      "No chapters yet. Generating them splits the meeting into sections you can jump between, using your workspace's model.",
    loadingTranscript: "Loading transcript",
    generate: "Generate chapters",
    count_one: "{{count}} chapter",
    count_other: "{{count}} chapters",
    trackLabel: "Chapters",
  },
  player: {
    transcriptTrack: "Transcript",
    jumpToCurrent: "Jump to current",
  },
  transcript: {
    empty: "No transcript yet. It appears once processing finishes.",
    renameSpeaker: "Rename this speaker everywhere in the transcript",
    changeSpeakerLabel: "Change who said \"{{text}}\"",
    changeSpeakerTitle: "Attribute this line to a different speaker",
    menuLabel: "Attribute this line to a speaker",
    menuHeading: "Attribute this line to",
    current: "current",
    someoneElse: "Someone else…",
    newSpeakerLabel: "Name a new speaker",
  },
  versions: {
    button: "Versions",
    close: "Close versions",
    loading: "Loading history",
    empty: "No versions yet. Each generation or publish saves one here.",
    current: "current",
    restore: "Restore",
  },
  translate: {
    dialogLabel: "Translate transcript",
    title: "Translate to {{language}}?",
    bodyFrom:
      "This uses your workspace's AI model to translate the transcript from {{source}} to {{target}}. It runs once, is stored with the recording, and can be cancelled while it runs.",
    bodyOriginal:
      "This uses your workspace's AI model to translate the transcript from its original language to {{target}}. It runs once, is stored with the recording, and can be cancelled while it runs.",
    confirm: "Translate",
  },
  status: {
    stalled: "The file never finished uploading. Delete this and try again.",
    transcribing: "Transcribing — this page updates when it is done.",
  },
} as const;

export default recording;
